import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Bot,
  Cpu,
  FileText,
  Film,
  LayoutDashboard,
  Mic,
  ShieldCheck,
  Sparkles,
  UserCheck,
  Video
} from 'lucide-react';
import ParticlesBackground from '../components/ParticlesBackground';
import LanguageSwitcher from '../components/LanguageSwitcher';
import nexbotSpec from '../text/text.txt';

const NEXBOT_APP_URL = 'https://www.bitrix24.es/apps/app/nextcoders.nexbot/';
const NEXBOT_CONTACT_URL = '/nexbot-contact';

const PageHeader = () => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <Link to="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders"; }}
        />
      </Link>
      <div className="flex items-center gap-4 md:gap-6">
        <LanguageSwitcher variant="compact" />
        <Link to="/nexbot-eula" className="text-gray-600 hover:text-[#141414] font-medium text-sm transition hidden md:block">
          EULA
        </Link>
        <Link to="/nexbot-privacidad" className="text-gray-600 hover:text-[#141414] font-medium text-sm transition hidden md:block">
          Privacidad
        </Link>
      </div>
    </div>
  </header>
);

const ICON_TONES = {
  slate: 'bg-gray-50 text-gray-900 border-gray-200',
  indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  amber: 'bg-amber-50 text-amber-700 border-amber-100',
  violet: 'bg-violet-50 text-violet-700 border-violet-100',
  sky: 'bg-sky-50 text-sky-700 border-sky-100'
};

const IconBadge = ({ icon: Icon, tone = 'slate', className = '' }) => (
  <span className={`flex items-center justify-center h-11 w-11 rounded-2xl border shadow-sm ${ICON_TONES[tone] || ICON_TONES.slate} ${className}`}>
    <Icon size={18} />
  </span>
);

const FeatureCard = ({ icon: Icon, title, description, chips }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-6 hover:shadow-lg transition-shadow duration-300">
    <div className="flex items-start gap-4">
      <IconBadge icon={Icon} tone="slate" className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-lg font-semibold text-gray-900">{title}</div>
        {!!description && <div className="mt-2 text-sm text-gray-600 leading-relaxed">{description}</div>}
        {!!chips?.length && (
          <div className="mt-4 flex flex-wrap gap-2">
            {chips.map((chip) => (
              <span key={chip} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-200 bg-gray-50 text-gray-700">
                {chip}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

const DashboardAccent = () => (
  <div className="h-12 w-12 rounded-2xl border border-indigo-100 bg-indigo-50 flex items-center justify-center" role="img" aria-label="Indicador de dashboard">
    <div className="flex items-end gap-1">
      {[7, 10, 13].map((height, index) => (
        <span key={`${height}-${index}`} className="w-1.5 rounded-full bg-indigo-500/80" style={{ height: `${height}px` }} />
      ))}
    </div>
  </div>
);

const LineSparkline = ({ className }) => (
  <svg viewBox="0 0 140 44" className={className} role="img" aria-label="Gráfico de referencia">
    <defs>
      <linearGradient id="lineRef" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#111827" stopOpacity="0.85" />
        <stop offset="100%" stopColor="#111827" stopOpacity="0.35" />
      </linearGradient>
      <linearGradient id="areaRef" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#111827" stopOpacity="0.18" />
        <stop offset="100%" stopColor="#111827" stopOpacity="0" />
      </linearGradient>
    </defs>
    <path d="M 8 30 L 30 26 L 54 28 L 78 20 L 102 18 L 132 14 L 132 40 L 8 40 Z" fill="url(#areaRef)" />
    <path d="M 8 30 L 30 26 L 54 28 L 78 20 L 102 18 L 132 14" fill="none" stroke="url(#lineRef)" strokeWidth="3" strokeLinecap="round" />
    {[{ x: 8, y: 30 }, { x: 30, y: 26 }, { x: 54, y: 28 }, { x: 78, y: 20 }, { x: 102, y: 18 }, { x: 132, y: 14 }].map((p) => (
      <circle key={`${p.x}-${p.y}`} cx={p.x} cy={p.y} r="3" fill="#111827" />
    ))}
  </svg>
);

const DonutRef = ({ className }) => (
  <svg viewBox="0 0 80 80" className={className} role="img" aria-label="Gráfico de referencia">
    <circle cx="40" cy="40" r="24" fill="none" stroke="#e5e7eb" strokeWidth="10" />
    <circle
      cx="40"
      cy="40"
      r="24"
      fill="none"
      stroke="#111827"
      strokeWidth="10"
      strokeDasharray="88 62"
      strokeLinecap="round"
      transform="rotate(-90 40 40)"
    />
  </svg>
);

const HeroMetricCard = ({ icon: Icon, tone = 'slate', title, value, hint, chart }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-6 min-h-[180px] h-full">
    <div className="flex items-start gap-3">
      <IconBadge icon={Icon} tone={tone} className="h-12 w-12 rounded-xl shadow-none flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-gray-900">{title}</div>
        <div className="mt-1 text-xs text-gray-500 leading-snug max-w-[170px]">{hint}</div>
      </div>
    </div>
    <div className="mt-5 flex items-end justify-between gap-4">
      <div className="min-w-0 text-2xl font-semibold text-[#141414] leading-tight">{value}</div>
      <div className="flex-shrink-0 self-end">{chart}</div>
    </div>
  </div>
);

const IncludedFeatureCard = ({ icon: Icon, tone = 'slate', title, description }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-4">
    <div className="flex items-start gap-3">
      <IconBadge icon={Icon} tone={tone} className="h-10 w-10 rounded-xl shadow-none flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-sm font-semibold text-gray-900">{title}</div>
        <div className="mt-1 text-sm text-gray-600 leading-relaxed">{description}</div>
      </div>
    </div>
  </div>
);

const normalizeText = (value) => (value || '').toString().trim().toLowerCase();
const toArray = (value, fallback = []) => (Array.isArray(value) ? value : fallback);
const toObject = (value, fallback = {}) => (value && typeof value === 'object' && !Array.isArray(value) ? value : fallback);

const findSectionByNeedles = (sections, needles) => {
  const n = Array.isArray(needles) ? needles : [needles];
  return (sections || []).find((section) => n.some((needle) => normalizeText(section.heading).includes(normalizeText(needle))));
};

const NexbotPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [specText, setSpecText] = useState('');
  const [activeTemplate, setActiveTemplate] = useState('');
  const [activeConfigTab, setActiveConfigTab] = useState('system');

  useEffect(() => {
    const load = async () => {
      const response = await fetch(nexbotSpec);
      const text = await response.text();
      setSpecText(text);
    };
    load();
  }, []);

  const spec = useMemo(() => {
    const raw = specText || '';
    const lines = raw
      .split(/\r?\n/)
      .map((line) => line.replace(/\s+$/, ''))
      .filter((line) => line.trim().length > 0);

    const title = (lines[0] || 'NexBot').trim();
    const sections = [];
    let current = null;

    const pushSection = (heading) => {
      current = { heading, description: '', items: [] };
      sections.push(current);
    };

    const isBulletLine = (line) => /^/.test(line.trim()) || /^o(\s|\t)/.test(line.trim()) || /^- /.test(line.trim());

    for (let index = 1; index < lines.length; index += 1) {
      const line = lines[index].trim();

      if (!isBulletLine(line)) {
        const isParagraph = line.endsWith('.') || line.length > 90;
        if (current && isParagraph) {
          current.description = current.description ? `${current.description} ${line}` : line;
        } else {
          pushSection(line);
        }
        continue;
      }

      if (!current) pushSection('Resumen');

      if (/^o(\s|\t)/.test(line)) {
        const text = line.replace(/^o(\s|\t)+/, '').trim();
        const parent = current.items[current.items.length - 1];
        if (parent) {
          parent.subitems = [...(parent.subitems || []), text];
        } else {
          current.items.push({ text });
        }
        continue;
      }

      const cleaned = line.replace(/^\s*/, '').replace(/^- /, '').trim();
      current.items.push({ text: cleaned });
    }

    return { title, sections };
  }, [specText]);

  const personalitySection = useMemo(
    () => findSectionByNeedles(spec.sections, ['personalidad e instrucciones', 'personalidad']),
    [spec.sections]
  );
  const variablesSection = useMemo(
    () => findSectionByNeedles(spec.sections, ['captura inteligente de variables', 'variables']),
    [spec.sections]
  );
  const transferSection = useMemo(
    () => findSectionByNeedles(spec.sections, ['reglas de transferencia a humano', 'transferencia a humano']),
    [spec.sections]
  );
  const transferOwnerSection = useMemo(
    () => findSectionByNeedles(spec.sections, ['transferencia automática al responsable', 'responsable']),
    [spec.sections]
  );
  const crmSection = useMemo(
    () => findSectionByNeedles(spec.sections, ['integración nativa con crm', 'crm']),
    [spec.sections]
  );

  const templates = useMemo(() => {
    const i18nTemplates = toArray(t('nexbot.personality.templates', { returnObjects: true }));
    if (i18nTemplates.length) return i18nTemplates;
    const item = personalitySection?.items?.find((entry) => normalizeText(entry.text).includes('sistema de plantillas'));
    const list = (item?.subitems || []).map((value) => value.trim()).filter(Boolean);
    if (list.length) return list;
    return ['Atención al cliente', 'Ventas', 'Soporte técnico', 'Sector inmobiliario, educativo, etc.'];
  }, [personalitySection, t]);

  const metrics = useMemo(() => toObject(t('nexbot.metrics', { returnObjects: true })), [t]);
  const dashboardRows = useMemo(() => toArray(t('nexbot.dashboard_section.rows', { returnObjects: true })), [t]);
  const managementFeatures = useMemo(() => toArray(t('nexbot.management.features', { returnObjects: true })), [t]);
  const architectureChips = useMemo(() => toArray(t('nexbot.architecture.chips', { returnObjects: true })), [t]);
  const transferCards = useMemo(() => toArray(t('nexbot.transfer_section.cards', { returnObjects: true })), [t]);
  const transferFlowSteps = useMemo(() => toArray(t('nexbot.transfer_section.flow_steps', { returnObjects: true })), [t]);
  const variableChips = useMemo(() => toArray(t('nexbot.variables_section.chips', { returnObjects: true })), [t]);
  const crmRows = useMemo(() => toArray(t('nexbot.variables_section.crm_rows', { returnObjects: true })), [t]);
  const dynamicLabels = useMemo(() => toArray(t('nexbot.variables_section.dynamic_labels', { returnObjects: true })), [t]);
  const crmFields = useMemo(() => toArray(t('nexbot.variables_section.crm_fields', { returnObjects: true })), [t]);
  const pricingItems = useMemo(() => toArray(t('nexbot.pricing.items', { returnObjects: true })), [t]);
  const onboardingItems = useMemo(() => toArray(t('nexbot.onboarding.items', { returnObjects: true })), [t]);
  const feedbackConfirmChips = useMemo(() => toArray(t('nexbot.feedback.confirm_chips', { returnObjects: true })), [t]);
  const overviewItems = useMemo(() => toArray(t('nexbot.overview.items', { returnObjects: true })), [t]);
  const knowledgeSection = useMemo(() => toObject(t('nexbot.knowledge_section', { returnObjects: true })), [t]);

  useEffect(() => {
    if (!activeTemplate && templates.length) setActiveTemplate(templates[0]);
  }, [activeTemplate, templates]);

  const tabConfig = useMemo(
    () => [
      { key: 'system', label: t('nexbot.personality.tabs.system', { defaultValue: 'System Prompt' }), icon: Sparkles, tone: 'violet' },
      { key: 'plantillas', label: t('nexbot.personality.tabs.plantillas', { defaultValue: 'Plantillas' }), icon: Bot, tone: 'indigo' },
      { key: 'variables', label: t('nexbot.personality.tabs.variables', { defaultValue: 'Variables' }), icon: Cpu, tone: 'sky' },
      { key: 'transfer', label: t('nexbot.personality.tabs.transfer', { defaultValue: 'Transferencia' }), icon: UserCheck, tone: 'emerald' },
      { key: 'crm', label: 'CRM', icon: LayoutDashboard, tone: 'amber' }
    ],
    [t]
  );

  const moduleDescriptions = useMemo(
    () => ({
      system: t('nexbot.personality.module_descriptions.system', {
        defaultValue: 'System Prompt personalizado: Editor dedicado para definir el comportamiento, tono y reglas del asistente.'
      }),
      plantillas: t('nexbot.personality.module_descriptions.plantillas', {
        defaultValue: 'Sistema de plantillas: Configuraciones predefinidas para distintos escenarios:'
      }),
      variables: t('nexbot.personality.module_descriptions.variables', {
        defaultValue: 'Captura Inteligente de Variables'
      }),
      transfer: t('nexbot.personality.module_descriptions.transfer', {
        defaultValue: 'Reglas de Transferencia a Humano'
      }),
      crm: t('nexbot.personality.module_descriptions.crm', {
        defaultValue: 'Integración Nativa con CRM'
      })
    }),
    [t]
  );

  const tabPayload = useMemo(() => {
    const systemItems = [
      t('nexbot.personality.system_prompt_desc', {
        defaultValue: 'System Prompt personalizado: Editor dedicado para definir el comportamiento, tono y reglas del asistente.'
      })
    ].filter(Boolean);
    const templateItems = templates.length
      ? templates
      : [t('nexbot.personality.templates_fallback', { defaultValue: 'Atención al cliente, Ventas, Soporte técnico, Sector inmobiliario, educativo, etc.' })];
    const variablesItems = [
      {
        text: t('nexbot.variables_section.desc_default', {
          defaultValue: 'Identificación automática de datos clave durante la conversación y sincronización transparente con Bitrix24.'
        })
      },
      {
        text: t('nexbot.variables_section.dynamic_step', {
          defaultValue: 'Mapeo automático de variables capturadas hacia campos del CRM.'
        }),
        subitems: variableChips
      },
      {
        text: t('nexbot.variables_section.crm_bridge', {
          defaultValue: 'Integración nativa con CRM para crear o actualizar contactos y negocios sin fricción.'
        })
      }
    ];
    const transferParagraphs = [
      t('nexbot.transfer_section.desc_default', {
        defaultValue: 'Configuración de condiciones bajo las cuales el bot detiene la automatización y transfiere la conversación a un agente humano dentro de Bitrix24.'
      })
    ].filter(Boolean);
    const transferItems = transferCards
      .map((card) => ({ text: card?.desc }))
      .filter((item) => item.text);
    const crmItems = crmRows.map((row) => ({ text: row }));

    return {
      system: {
        title: t('nexbot.personality.system_prompt_desc', {
          defaultValue: 'System Prompt personalizado: Editor dedicado para definir el comportamiento, tono y reglas del asistente.'
        }),
        paragraphs: [],
        items: systemItems
      },
      plantillas: {
        title: t('nexbot.personality.templates_title', { defaultValue: 'Sistema de plantillas' }),
        paragraphs: [],
        items: templateItems
      },
      variables: {
        title: t('nexbot.variables_section.title', { defaultValue: 'Captura Inteligente de Variables' }),
        paragraphs: [],
        items: variablesItems
      },
      transfer: {
        title: t('nexbot.transfer_section.title', { defaultValue: 'Reglas de Transferencia a Humano' }),
        paragraphs: transferParagraphs,
        items: transferItems
      },
      crm: {
        title: t('nexbot.personality.tabs.crm', { defaultValue: 'CRM' }),
        paragraphs: [],
        items: crmItems
      }
    };
  }, [crmRows, t, templates, transferCards, variableChips]);

  const renderConfigContent = () => {
    if (activeConfigTab === 'system') {
      return (
        <div className="rounded-3xl border border-gray-200 bg-gray-50 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Editor del system prompt</div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Rol y contexto del bot',
              'Objetivo principal de la conversación',
              'Tono, personalidad y estilo de respuesta',
              'Reglas, límites y criterios de escalamiento'
            ].map((item) => (
              <div key={item} className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
                {item}
              </div>
            ))}
          </div>
          <div className="mt-4 text-sm text-gray-600 leading-relaxed">
            {tabPayload.system?.title}
          </div>
        </div>
      );
    }

    if (activeConfigTab === 'plantillas') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((template) => {
            const isSelected = template === activeTemplate;
            return (
              <button
                key={template}
                type="button"
                onClick={() => setActiveTemplate(template)}
                className={`text-left rounded-2xl border p-4 transition ${
                  isSelected ? 'bg-indigo-50 border-indigo-200 shadow-sm' : 'bg-gray-50 border-gray-200 hover:bg-white'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className={`text-sm font-semibold ${isSelected ? 'text-indigo-900' : 'text-gray-900'}`}>{template}</div>
                  {isSelected && (
                    <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-indigo-200 text-indigo-700">
                      Activa
                    </span>
                  )}
                </div>
                <div className={`mt-2 text-sm leading-relaxed ${isSelected ? 'text-indigo-800' : 'text-gray-600'}`}>
                  Plantilla lista para usar y adaptar a tu bot sin rehacer la configuración desde cero.
                </div>
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <div className="mt-4 space-y-3">
        {(tabPayload[activeConfigTab]?.items || []).map((item) => {
          if (typeof item === 'string') {
            return (
              <div key={item} className="flex items-start gap-3 rounded-2xl border p-4 bg-gray-50 border-gray-200">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gray-900 flex-shrink-0" />
                <div className="text-sm leading-relaxed text-gray-700">{item}</div>
              </div>
            );
          }

          const key = item.text;
          return (
            <div key={key} className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gray-900 flex-shrink-0" />
                <div className="text-sm text-gray-700 leading-relaxed">{item.text}</div>
              </div>
              {!!item.subitems?.length && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.subitems.map((sub) => (
                    <span key={sub} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-200 bg-white text-gray-700">
                      {sub}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="relative min-h-screen bg-transparent font-sans text-gray-800 selection:bg-blue-100">
      <ParticlesBackground />

      <PageHeader />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back', { defaultValue: 'Volver' })}
          </button>

          <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 mb-10">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)] gap-6 items-start">
              <div className="min-w-0 lg:max-w-[29rem]">
                <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-800 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-gray-200">
                  <Bot size={16} />
                  {t('nexbot.badge', { defaultValue: 'Chatbot + Bitrix24' })}
                </div>
                <img
                  src="/images/nexbot/icono.png"
                  alt="NexBot logo"
                  className="h-auto w-full max-w-[180px] md:max-w-[220px] mb-4"
                />
                <h1 className="sr-only">
                  NexBot
                </h1>
                <div className="text-sm font-semibold text-gray-500 mb-5">
                  {spec.title}
                </div>
                <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl">
                  {t('nexbot.subtitle', { defaultValue: 'Plataforma inteligente de automatización conversacional para Bitrix24: administra bots, configura IA, captura variables y transfiere a humano con control total.' })}
                </p>
              </div>

              <div className="w-full min-w-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <HeroMetricCard
                    icon={LayoutDashboard}
                    tone="indigo"
                    title={metrics.dashboard?.title || 'Dashboard'}
                    value={metrics.dashboard?.value || 'Gestión centralizada'}
                    hint={metrics.dashboard?.hint || 'Supervisión en tiempo real'}
                    chart={<DashboardAccent />}
                  />
                  <HeroMetricCard
                    icon={UserCheck}
                    tone="emerald"
                    title={metrics.transfer?.title || 'Transferencia'}
                    value={metrics.transfer?.value || 'Reglas a humano'}
                    hint={metrics.transfer?.hint || 'Escalamiento controlado'}
                    chart={<DonutRef className="h-12 w-12" />}
                  />
                  <div className="sm:col-span-2">
                    <HeroMetricCard
                      icon={Cpu}
                      tone="violet"
                      title={metrics.models?.title || 'Modelos y proveedor'}
                      value={metrics.models?.value || 'GPT-4 · Claude · Llama'}
                      hint={metrics.models?.hint || 'Multi-modelo vía OpenRouter'}
                      chart={<LineSparkline className="h-11 w-[140px]" />}
                    />
                  </div>
                </div>
                <div className="mt-10 flex flex-col sm:flex-row gap-3 w-full">
                  <a
                    href={NEXBOT_APP_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 bg-[#141414] text-white hover:bg-gray-900 font-bold py-2.5 px-6 rounded-xl transition text-sm md:text-base text-center"
                  >
                    {t('nexbot.cta_demo', { defaultValue: 'Solicitar demo' })}
                  </a>
                  <a
                    href="#dashboard"
                    className="flex-1 bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-2.5 px-6 rounded-xl transition text-center text-sm md:text-base"
                  >
                    {t('nexbot.cta_dashboard', { defaultValue: 'Ver dashboard' })}
                  </a>
                </div>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {(overviewItems.length ? overviewItems : [
                { title: 'Modelos IA libres', desc: 'Conecta GPT, Claude, Gemini y mas con tu propia clave.' },
                { title: 'RAG con archivos', desc: 'Sube documentos y consulta solo el contexto util para ahorrar tokens.' },
                { title: 'Audio a texto', desc: 'Tus clientes envian audios y NexBot los entiende para responder en texto.' },
                { title: 'Escalamiento humano', desc: 'Transfiere conversaciones a Bitrix24 con reglas y contexto completo.' }
              ]).map((item, index) => (
                <IncludedFeatureCard
                  key={`${item.title}-${index}`}
                  icon={[Cpu, FileText, Mic, UserCheck][index] || Bot}
                  tone={['violet', 'indigo', 'amber', 'emerald'][index] || 'slate'}
                  title={item.title}
                  description={item.desc}
                />
              ))}
            </div>
          </div>

          <section id="dashboard" className="mb-16">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-800 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-gray-200">
                    <LayoutDashboard size={16} />
                    {t('nexbot.dashboard_section.badge', { defaultValue: 'Dashboard' })}
                  </div>
                  <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('nexbot.dashboard_section.title', { defaultValue: 'Control y gestión centralizada' })}</h2>
                  <p className="text-gray-600 leading-relaxed max-w-3xl">
                    {t('nexbot.dashboard_section.desc', { defaultValue: 'Panel centralizado desde donde se administran todos los asistentes virtuales creados. Permite una supervisión clara, ordenada y en tiempo real del ecosistema de bots activos.' })}
                  </p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-3xl p-6 w-full lg:max-w-sm">
                  <div className="text-sm font-semibold text-gray-900 mb-4">{t('nexbot.dashboard_section.card_title', { defaultValue: 'Tarjetas de estado dinámicas' })}</div>
                  <div className="space-y-3">
                    {(dashboardRows.length ? dashboardRows : ['Estado actual (Activo / Inactivo)', 'Modelo de IA configurado', 'Proveedor seleccionado']).map((row) => (
                      <div key={row} className="bg-white border border-gray-200 rounded-2xl p-4 flex items-start gap-3">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gray-900 flex-shrink-0" />
                        <div className="text-sm text-gray-700 leading-relaxed">{row}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-800 px-4 py-2 rounded-full text-sm font-semibold border border-gray-200">
                    <Bot size={16} />
                    {t('nexbot.management.badge', { defaultValue: 'Gestión de bots' })}
                  </div>
                  <h2 className="mt-5 text-3xl font-bold text-[#141414]">{t('nexbot.management.title', { defaultValue: 'Administración segura y rápida' })}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(managementFeatures.length ? managementFeatures : [
                    { title: 'Creación intuitiva', desc: 'Configuración rápida de nuevos bots definiendo nombre.' },
                    { title: 'Eliminación segura', desc: 'Eliminación con confirmación de seguridad para evitar acciones accidentales.' },
                    { title: 'Estado y configuración', desc: 'Tarjetas que muestran estado actual, modelo configurado y proveedor seleccionado.', chips: ['Activo / Inactivo', 'Modelo', 'Proveedor'] },
                    { title: 'Transferencia al responsable', desc: 'Si el contacto existe en CRM, la conversación se asigna al responsable configurado del cliente.' }
                  ]).map((feature, idx) => (
                    <FeatureCard
                      key={feature.title || idx}
                      icon={[Bot, ShieldCheck, LayoutDashboard, UserCheck][idx] || Bot}
                      title={feature.title}
                      description={feature.desc}
                      chips={feature.chips}
                    />
                  ))}
                </div>
              </div>

              <div className="bg-[#141414] text-white rounded-3xl p-8 md:p-10 relative overflow-hidden border border-white/10">
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-white/5 to-transparent" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                    <Cpu size={16} />
                    {t('nexbot.architecture.badge', { defaultValue: 'Modelos y proveedores' })}
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{t('nexbot.architecture.title', { defaultValue: 'Arquitectura multi-proveedor' })}</h3>
                  <p className="text-white/80 leading-relaxed mb-6">
                    {t('nexbot.architecture.desc', { defaultValue: 'Compatible con servicios como OpenRouter y con soporte multi-modelo para cambiar según necesidades de rendimiento, costo o precisión.' })}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(architectureChips.length ? architectureChips : ['GPT-4', 'Claude', 'Llama', 'OpenRouter']).map((chip) => (
                      <span key={chip} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white/10 border border-white/15 text-white/90">
                        {chip}
                      </span>
                    ))}
                  </div>
                  <a
                    href="/nexbot-openrouter"
                    className="inline-flex items-center mt-6 text-sm font-semibold text-white hover:text-white/80 transition underline underline-offset-4"
                  >
                    Ver guia para crear cuenta y API key en OpenRouter
                  </a>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
              <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-800 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-gray-200">
                    <Sparkles size={16} />
                    {t('nexbot.personality.badge', { defaultValue: 'Personalidad e instrucciones' })}
                  </div>
                  <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('nexbot.personality.title', { defaultValue: 'System Prompt y plantillas' })}</h2>
                  <p className="text-gray-600 leading-relaxed max-w-3xl">
                    {t('nexbot.personality.desc', { defaultValue: 'Un editor dedicado para definir comportamiento, tono y reglas del asistente, junto con plantillas predefinidas para distintos escenarios.' })}
                  </p>
                  <div className="mt-6 bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm">
                    <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center justify-between gap-4">
                        <div className="text-sm font-semibold text-gray-900">{t('nexbot.personality.configuration_title', { defaultValue: 'Configuración' })}</div>
                        {activeConfigTab === 'plantillas' && !!activeTemplate && (
                          <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-gray-200 text-gray-700">
                            {activeTemplate}
                          </span>
                        )}
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {tabConfig.map((tab) => {
                          const isActive = tab.key === activeConfigTab;
                          const TabIcon = tab.icon;
                          return (
                            <button
                              key={tab.key}
                              type="button"
                              onClick={() => setActiveConfigTab(tab.key)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold border transition ${
                                isActive ? 'bg-[#141414] border-[#141414] text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <span className={`flex items-center justify-center h-7 w-7 rounded-xl border ${isActive ? 'border-white/15 bg-white/10' : ICON_TONES[tab.tone]}`}>
                                <TabIcon size={14} className={isActive ? 'text-white' : ''} />
                              </span>
                              {tab.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="rounded-3xl bg-white border border-gray-200 p-5">
                        <div className="text-lg font-semibold text-gray-900">{tabPayload[activeConfigTab]?.title}</div>
                        {!!tabPayload[activeConfigTab]?.paragraphs?.length && (
                          <div className="mt-2 space-y-2">
                            {tabPayload[activeConfigTab].paragraphs.map((p) => (
                              <div key={p} className="text-sm text-gray-600 leading-relaxed">
                                {p}
                              </div>
                            ))}
                          </div>
                        )}
                        {renderConfigContent()}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 bg-white border border-gray-200 rounded-3xl p-5">
                    <div className="text-sm font-semibold text-gray-900 mb-4">{t('nexbot.personality.summary_title', { defaultValue: 'Resumen visible de módulos' })}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {tabConfig.map((tab) => (
                        <div key={`summary-${tab.key}`} className="flex items-start justify-between gap-3 bg-gray-50 border border-gray-200 rounded-2xl p-3">
                          <div className="flex items-start gap-3 min-w-0">
                            <IconBadge icon={tab.icon} tone={tab.tone} className="h-9 w-9 rounded-xl shadow-none flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-900">{tab.label}</div>
                              <div className="text-xs text-gray-600 leading-relaxed">{(moduleDescriptions[tab.key] || '').toString()}</div>
                            </div>
                          </div>
                          <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-gray-200 text-gray-700">
                            {(tabPayload[tab.key]?.items || []).length}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
          </section>

          <section className="mb-16">
            <div className="space-y-8">
              <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
                <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-emerald-100">
                  <UserCheck size={16} />
                  {t('nexbot.transfer_section.badge', { defaultValue: 'Transferencia a humano' })}
                </div>
                <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('nexbot.transfer_section.title', { defaultValue: 'Reglas claras de escalamiento' })}</h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {t('nexbot.transfer_section.desc_default', { defaultValue: transferSection?.description || 'Configuración de condiciones bajo las cuales el bot detiene la automatización y transfiere la conversación a un agente humano dentro de Bitrix24.' })}
                </p>
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-white border border-gray-200 rounded-3xl p-6">
                    <div className="flex items-start gap-3">
                      <IconBadge icon={UserCheck} tone="emerald" className="h-10 w-10 rounded-xl shadow-none flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900">{transferCards[0]?.title || t('nexbot.transfer_section.cards.0.title', { defaultValue: 'Reglas de transferencia a humano' })}</div>
                        <div className="mt-2 text-sm text-gray-600 leading-relaxed">
                          {transferCards[0]?.desc || t('nexbot.transfer_section.cards.0.desc', { defaultValue: transferSection?.description || 'Configuración de condiciones bajo las cuales el bot detiene la automatización y transfiere la conversación a un agente humano dentro de Bitrix24.' })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-3xl p-6">
                    <div className="flex items-start gap-3">
                      <IconBadge icon={LayoutDashboard} tone="amber" className="h-10 w-10 rounded-xl shadow-none flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900">{transferCards[1]?.title || t('nexbot.transfer_section.cards.1.title', { defaultValue: 'Transferencia automática al responsable' })}</div>
                        <div className="mt-2 text-sm text-gray-600 leading-relaxed">
                          {transferCards[1]?.desc || t('nexbot.transfer_section.cards.1.desc', { defaultValue: transferOwnerSection?.items?.[0]?.text || 'Al activar esta opción, el bot verificará si el cliente ya existe en el CRM mediante su correo electrónico.' })}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-3xl p-6">
                    <div className="text-sm font-semibold text-gray-900 mb-4">{t('nexbot.transfer_section.flow_title', { defaultValue: 'Flujo de escalamiento' })}</div>
                    <div className="space-y-3">
                      {[
                        { title: transferFlowSteps[0]?.title || 'Condiciones', subtitle: transferFlowSteps[0]?.subtitle || transferCards[0]?.desc || transferSection?.description || 'Configuración de condiciones bajo las cuales el bot detiene la automatización y transfiere la conversación a un agente humano dentro de Bitrix24.', tone: 'emerald', icon: UserCheck },
                        { title: transferFlowSteps[1]?.title || 'Agente humano', subtitle: transferFlowSteps[1]?.subtitle || 'Transfiere la conversación a un agente humano dentro de Bitrix24.', tone: 'sky', icon: UserCheck },
                        { title: transferFlowSteps[2]?.title || 'Responsable del cliente', subtitle: transferFlowSteps[2]?.subtitle || transferOwnerSection?.items?.[1]?.text || 'Si el contacto está registrado en Bitrix24, la conversación se asignará automáticamente al usuario configurado como Responsable del cliente, evitando que pase por la cola general de atención.', tone: 'amber', icon: LayoutDashboard }
                      ].map((step) => (
                        <div key={step.title} className="flex items-start gap-4">
                          <div className="flex flex-col items-center">
                            <IconBadge icon={step.icon} tone={step.tone} className="h-10 w-10 rounded-xl shadow-none" />
                            <div className="mt-2 h-8 w-px bg-gray-200" />
                          </div>
                          <div className="bg-gray-50 border border-gray-200 rounded-3xl p-4 flex-1">
                            <div className="text-sm font-semibold text-gray-900">{step.title}</div>
                            <div className="mt-1 text-sm text-gray-600 leading-relaxed">{step.subtitle}</div>
                          </div>
                        </div>
                      ))}
                      <div className="flex items-start gap-4">
                        <div className="flex flex-col items-center">
                          <IconBadge icon={LayoutDashboard} tone="slate" className="h-10 w-10 rounded-xl shadow-none" />
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-3xl p-4 flex-1">
                          <div className="text-sm font-semibold text-gray-900">{transferFlowSteps[3]?.title || 'Bitrix24'}</div>
                          <div className="mt-1 text-sm text-gray-600 leading-relaxed">{transferFlowSteps[3]?.subtitle || 'Dentro de Bitrix24.'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
                <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-800 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-gray-200">
                  <Cpu size={16} />
                  {t('nexbot.variables_section.badge', { defaultValue: 'Variables y CRM' })}
                </div>
                <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('nexbot.variables_section.title', { defaultValue: 'Datos estructurados sin fricción' })}</h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {t('nexbot.variables_section.desc_default', { defaultValue: variablesSection?.items?.[0]?.text || 'Identificación automática de datos clave durante la conversación y sincronización transparente con Bitrix24.' })}
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {(variableChips.length ? variableChips : (variablesSection?.items?.find((entry) => entry.subitems?.length)?.subitems || ['Servicio', 'Código producto', 'Interés del cliente'])).map((chip) => (
                    <span key={chip} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-200 bg-white text-gray-700">
                      {chip}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {(crmRows.length ? crmRows : (crmSection?.items?.length ? crmSection.items.map((row) => row.text) : [
                    'Identificación automática de clientes existentes.',
                    'Creación o actualización de contactos y negocios.',
                    'Sustitución automática de variables capturadas en campos del CRM (título, comentarios, etc.).',
                    'Sincronización transparente con el entorno de trabajo de Bitrix24.'
                  ])).map((row) => (
                    <div key={row} className="bg-white border border-gray-200 rounded-3xl p-5 flex items-start gap-3">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gray-900 flex-shrink-0" />
                      <div className="text-sm text-gray-700 leading-relaxed">{row}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-white border border-gray-200 rounded-3xl p-6">
                  <div className="text-sm font-semibold text-gray-900 mb-4">{t('nexbot.variables_section.dynamic_title', { defaultValue: 'Variables dinámicas → CRM' })}</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                    <div className="bg-gray-50 border border-gray-200 rounded-3xl p-4">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{dynamicLabels[0] || 'Variables'}</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(variableChips.length ? variableChips : (variablesSection?.items?.find((entry) => entry.subitems?.length)?.subitems || ['Servicio', 'Código producto', 'Interés del cliente'])).map((chip) => (
                          <span key={chip} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-200 bg-white text-gray-700">
                            {chip}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-3xl p-4">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{dynamicLabels[1] || 'Paso intermedio'}</div>
                      <div className="mt-3 text-sm text-gray-700 leading-relaxed">
                        {t('nexbot.variables_section.dynamic_step', { defaultValue: 'Mapeo automático de variables capturadas hacia campos del CRM.' })}
                      </div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-3xl p-4">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{dynamicLabels[2] || 'Campos CRM'}</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(crmFields.length ? crmFields : ['Contactos', 'Negocios', 'Título', 'Comentarios']).map((chip) => (
                          <span key={chip} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-200 bg-white text-gray-700">
                            {chip}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
              <div className="max-w-4xl">
                <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-800 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-gray-200">
                  <FileText size={16} />
                  {knowledgeSection.badge || 'Archivos, RAG y audio'}
                </div>
                <h2 className="text-3xl font-bold text-[#141414] mb-4">
                  {knowledgeSection.title || 'Más contexto útil, menos consumo innecesario'}
                </h2>
                <p className="text-gray-600 leading-relaxed max-w-3xl">
                  {knowledgeSection.desc || 'NexBot ya no solo responde: también consulta archivos, arma contexto útil antes de llamar al modelo y entiende audios enviados por clientes.'}
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="rounded-3xl border border-gray-200 bg-gradient-to-br from-indigo-50 via-white to-slate-50 p-6">
                  <div className="flex items-start gap-4">
                    <IconBadge icon={FileText} tone="indigo" className="h-12 w-12 rounded-xl shadow-none flex-shrink-0" />
                    <div className="min-w-0">
                      <h3 className="text-2xl font-bold text-[#141414]">
                        {knowledgeSection.rag?.title || 'Carga de archivos y RAG inteligente'}
                      </h3>
                      <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                        {knowledgeSection.rag?.desc || 'Sube documentos y deja que NexBot recupere solo el contexto necesario antes de consultar al modelo, reduciendo consumo y mejorando precisión.'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {(knowledgeSection.rag?.items || []).map((item) => (
                      <span key={item} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-indigo-100 bg-white text-indigo-900">
                        {item}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 bg-white border border-gray-200 rounded-3xl p-5">
                    <div className="grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-4">
                      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {knowledgeSection.rag?.mockup?.files_label || 'Archivos cargados'}
                        </div>
                        <div className="mt-3 space-y-2">
                          {(knowledgeSection.rag?.mockup?.files || []).map((file) => (
                            <div key={file} className="flex items-center justify-between gap-3 rounded-2xl bg-white border border-gray-200 px-3 py-2 text-sm text-gray-700">
                              <span>{file}</span>
                              <span className="text-[11px] font-semibold text-emerald-700">
                                {knowledgeSection.rag?.mockup?.indexed_label || 'Indexado'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-2xl border border-gray-200 bg-[#141414] p-4 text-white">
                        <div className="text-xs font-semibold uppercase tracking-wide text-white/60">
                          {knowledgeSection.rag?.mockup?.retrieval_label || 'RAG activo'}
                        </div>
                        <div className="mt-3 space-y-3">
                          {(knowledgeSection.rag?.mockup?.steps || []).map((step) => (
                            <div key={step} className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/90">
                              {step}
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 rounded-2xl bg-white/10 border border-white/10 px-4 py-3">
                          <div className="text-[11px] uppercase tracking-wide text-white/60">
                            {knowledgeSection.rag?.mockup?.savings_label || 'Ahorro estimado de tokens'}
                          </div>
                          <div className="mt-1 text-xl font-bold">
                            {knowledgeSection.rag?.mockup?.savings_value || '-60% en contexto innecesario'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-gray-200 bg-gradient-to-br from-amber-50 via-white to-slate-50 p-6">
                  <div className="flex items-start gap-4">
                    <IconBadge icon={Mic} tone="amber" className="h-12 w-12 rounded-xl shadow-none flex-shrink-0" />
                    <div className="min-w-0">
                      <h3 className="text-2xl font-bold text-[#141414]">
                        {knowledgeSection.audio?.title || 'Transcripción de audio a texto'}
                      </h3>
                      <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                        {knowledgeSection.audio?.desc || 'Tus clientes pueden enviar audios y NexBot los convierte en texto para entender la intención, responder con claridad y mantener el CRM ordenado.'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {(knowledgeSection.audio?.items || []).map((item) => (
                      <span key={item} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-amber-100 bg-white text-amber-900">
                        {item}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 bg-white border border-gray-200 rounded-3xl p-5">
                    <div className="grid grid-cols-1 gap-4">
                      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {knowledgeSection.audio?.mockup?.inbox_label || 'Entrada del cliente'}
                        </div>
                        <div className="mt-3 flex items-center gap-3 rounded-2xl bg-white border border-gray-200 px-4 py-3">
                          <span className="flex items-center justify-center h-10 w-10 rounded-full bg-amber-100 text-amber-700">
                            <Mic size={18} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-900">
                              {knowledgeSection.audio?.mockup?.audio_label || 'Audio del cliente'}
                            </div>
                            <div className="mt-2 flex items-end gap-1 h-8">
                              {[10, 18, 12, 20, 14, 24, 16, 10].map((bar, index) => (
                                <span key={`${bar}-${index}`} className="w-2 rounded-full bg-amber-400/80" style={{ height: `${bar}px` }} />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-gray-200 bg-white p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {knowledgeSection.audio?.mockup?.transcript_label || 'Texto transcrito'}
                        </div>
                        <div className="mt-3 rounded-2xl bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-700 leading-relaxed">
                          {knowledgeSection.audio?.mockup?.transcript_text || 'Hola, quiero saber si el pedido 4312 ya fue enviado y cuándo me responderá un asesor.'}
                        </div>
                      </div>

                      <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                          {knowledgeSection.audio?.mockup?.reply_label || 'Respuesta del bot'}
                        </div>
                        <div className="mt-3 text-sm text-emerald-900 leading-relaxed">
                          {knowledgeSection.audio?.mockup?.reply_text || 'Ya entendí tu audio, revisaré el pedido 4312 y te responderé aquí mismo en texto con el siguiente paso.'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-violet-50 via-white to-slate-50 border border-gray-200 rounded-3xl p-8 md:p-12">
                <div className="inline-flex items-center gap-2 bg-violet-50 text-violet-700 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-violet-100">
                  <Video size={16} />
                  {t('nexbot.onboarding.badge', { defaultValue: 'Centro de ayuda' })}
                </div>
                <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('nexbot.onboarding.title', { defaultValue: 'Onboarding simple' })}</h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {t('nexbot.onboarding.desc', { defaultValue: 'Sección de tutoriales en video directamente accesible desde el dashboard, con curva de aprendizaje reducida para nuevos usuarios.' })}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white border border-gray-200 rounded-3xl p-5">
                    <div className="text-sm font-semibold text-gray-900">{onboardingItems[0]?.title || 'Acceso desde dashboard'}</div>
                    <div className="mt-2 text-sm text-gray-600 leading-relaxed">{onboardingItems[0]?.desc || 'Centro de ayuda integrado dentro de NexBot para consultar tutoriales en video.'}</div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-3xl p-5">
                    <div className="text-sm font-semibold text-gray-900">{onboardingItems[1]?.title || 'Curva de aprendizaje reducida'}</div>
                    <div className="mt-2 text-sm text-gray-600 leading-relaxed">{onboardingItems[1]?.desc || 'Facilita adopción de nuevos usuarios y acelera uso operativo del sistema.'}</div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-amber-50 via-white to-slate-50 border border-gray-200 rounded-3xl p-8 md:p-12">
                <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-amber-100">
                  <Film size={16} />
                  {t('nexbot.feedback.badge', { defaultValue: 'Notificaciones' })}
                </div>
                <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('nexbot.feedback.title', { defaultValue: 'Feedback claro' })}</h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {t('nexbot.feedback.desc', { defaultValue: 'Confirmaciones visuales de éxito (creación, guardado, conexión) y alertas ante errores de configuración o conexión.' })}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white border border-gray-200 rounded-3xl p-5">
                    <div className="text-sm font-semibold text-gray-900">{t('nexbot.feedback.confirm_title', { defaultValue: 'Confirmaciones visuales' })}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(feedbackConfirmChips.length ? feedbackConfirmChips : ['Creación', 'Guardado', 'Conexión']).map((chip) => (
                        <span key={chip} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-emerald-100 bg-emerald-50 text-emerald-700">
                          {chip}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-3xl p-5">
                    <div className="text-sm font-semibold text-gray-900">{t('nexbot.feedback.alerts_title', { defaultValue: 'Alertas claras' })}</div>
                    <div className="mt-2 text-sm text-gray-600 leading-relaxed">
                      {t('nexbot.feedback.alerts_desc', { defaultValue: 'Mensajes directos ante errores de configuración o conexión para corregir rápido.' })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
              <div className="max-w-4xl">
                <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-800 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-gray-200">
                  <Sparkles size={16} />
                  {t('nexbot.pricing.badge', { defaultValue: 'Precio' })}
                </div>
                <h2 className="text-3xl font-bold text-[#141414] mb-4">
                  {t('nexbot.pricing.title', { defaultValue: 'Un precio. Todo incluido.' })}
                </h2>
                <div className="text-3xl md:text-4xl font-extrabold text-[#141414] mb-6">
                  {t('nexbot.pricing.price_line', { defaultValue: '$19.90 USD al mes.' })}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {(pricingItems.length ? pricingItems : [
                    '5 bots activos simultaneos',
                    '5 prompts por bot',
                    '17 modelos de IA disponibles',
                    'Transferencias inteligentes al CRM',
                    'Creacion automatica de deals en Bitrix24',
                    'Soporte por email'
                  ]).map((item) => (
                    <div key={item} className="bg-gray-50 border border-gray-200 rounded-3xl p-4 flex items-start gap-3">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gray-900 flex-shrink-0" />
                      <div className="text-sm text-gray-700 leading-relaxed">{item}</div>
                    </div>
                  ))}
                </div>
                <p className="text-gray-700 font-medium mb-2">
                  {t('nexbot.pricing.outro', { defaultValue: 'Sin costo por bot adicional. Sin sorpresas.' })}
                </p>
                <p className="text-gray-600 mb-8">
                  {t('nexbot.pricing.trial', { defaultValue: 'Incluye periodo de prueba gratuito.' })}
                </p>
                <a
                  href={NEXBOT_APP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('nexbot.pricing.cta', { defaultValue: 'Instalar en Bitrix24' })}
                </a>
              </div>
            </div>
          </section>

          <div className="text-center">
            <a
              href={NEXBOT_APP_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-10 rounded-full transition"
            >
              {t('nexbot.final_cta', { defaultValue: 'Quiero NexBot en mi Bitrix24' })}
            </a>
            <div className="mt-4 text-sm text-gray-600">
              <div className="flex items-center justify-center gap-4">
                <a href={NEXBOT_CONTACT_URL} className="font-medium text-[#141414] hover:underline">
                  Contacto
                </a>
                <span className="text-gray-300">|</span>
                <a href="/nexbot-openrouter" className="font-medium text-[#141414] hover:underline">
                  Guia OpenRouter
                </a>
                <span className="text-gray-300">|</span>
                <a href="/nexbot-eula" className="font-medium text-[#141414] hover:underline">
                  Ver EULA de NexBot
                </a>
                <span className="text-gray-300">|</span>
                <a href="/nexbot-privacidad" className="font-medium text-[#141414] hover:underline">
                  Ver política de privacidad
                </a>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default NexbotPage;
